import React, { useEffect, useMemo, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
    LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip,
    ResponsiveContainer, BarChart, Bar, AreaChart, Area
} from 'recharts';
import { ArrowLeft, Wifi, Smartphone, Moon, Activity, Clock, BarChart3, Signal, ChevronDown } from 'lucide-react';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:3005';

type Range = '1m' | '5m' | '15m' | '30m' | '1h' | '12h' | '24h' | '7d' | '30d';

interface DetailsResponse {
    contact: {
        id: number;
        jid: string;
        number: string;
        customName: string | null;
        profilePicUrl?: string | null;
        lastKnownState?: string | null;
    };
    summary: null | {
        range: Range;
        sampleCount: number;
        avgRtt: number;
        onlinePct: number;
        standbyPct: number;
        offlinePct: number;
        firstOnlineToday: string | null;
        lastOnlineToday: string | null;
        peakHour: number | null;
    };
    network: {
        fastStable: number;
        mediumMobile: number;
        slowUnstable: number;
    };
    inactivityWindows: Array<{ start: string; end: string; hours: number }>;
    insights: string[];
    samples?: Array<{
        state: string;
        avgRtt: number;
        createdAt: string;
        networkProfile?: string;
        deviceJid?: string | null;
    }>;
    knownDevices?: string[];
    selectedDevice?: string | null;
}

function ProgressRing({ value, color, label, size = 100 }: { value: number; color: string; label: string; size?: number }) {
    const radius = (size - 12) / 2;
    const circumference = 2 * Math.PI * radius;
    const offset = circumference - (value / 100) * circumference;

    return (
        <div className="flex flex-col items-center gap-2">
            <div className="relative" style={{ width: size, height: size }}>
                <svg width={size} height={size} className="-rotate-90">
                    <circle cx={size / 2} cy={size / 2} r={radius} fill="none" stroke="rgba(255,255,255,0.08)" strokeWidth="6" />
                    <circle
                        cx={size / 2} cy={size / 2} r={radius} fill="none" stroke={color} strokeWidth="6"
                        strokeDasharray={circumference} strokeDashoffset={offset} strokeLinecap="round"
                        className="transition-all duration-1000"
                    />
                </svg>
                <div className="absolute inset-0 flex items-center justify-center">
                    <span className="text-lg font-bold text-white">{value}%</span>
                </div>
            </div>
            <span className="text-xs text-white/50">{label}</span>
        </div>
    );
}

function formatDeviceJid(jid: string): string {
    const match = jid.match(/:(\d+)@/);
    if (match) return `Device ${match[1]}`;
    const num = jid.split('@')[0];
    return `Device ${num.slice(-4)}`;
}

function buildHeatmapData(samples: DetailsResponse['samples'], range: Range) {
    if (!samples || samples.length === 0) return [];
    const heatmap: Record<string, Record<number, { online: number; total: number }>> = {};
    const days = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];

    for (const s of samples) {
        const d = new Date(s.createdAt);
        const day = days[d.getDay()];
        const hour = d.getHours();
        if (!heatmap[day]) heatmap[day] = {};
        if (!heatmap[day][hour]) heatmap[day][hour] = { online: 0, total: 0 };
        heatmap[day][hour].total++;
        if (s.state.includes('Online')) heatmap[day][hour].online++;
    }

    const result: Array<{ day: string; hour: number; intensity: number }> = [];
    for (const day of days) {
        for (let h = 0; h < 24; h++) {
            const cell = heatmap[day]?.[h];
            result.push({
                day,
                hour: h,
                intensity: cell ? Math.round((cell.online / cell.total) * 100) : 0
            });
        }
    }
    return result;
}

function buildPeakHoursData(samples: DetailsResponse['samples']) {
    if (!samples || samples.length === 0) return [];
    const hours: Record<number, number> = {};
    for (const s of samples) {
        const h = new Date(s.createdAt).getHours();
        hours[h] = (hours[h] || 0) + (s.state.includes('Online') ? 1 : 0);
    }
    return Array.from({ length: 24 }, (_, h) => ({ hour: `${String(h).padStart(2, '0')}:00`, count: hours[h] || 0 }));
}

function buildRttTimeline(samples: DetailsResponse['samples']) {
    if (!samples || samples.length === 0) return [];
    const step = Math.max(1, Math.floor(samples.length / 200));
    return samples.filter((_, i) => i % step === 0).map(s => ({
        time: new Date(s.createdAt).getTime(),
        rtt: Math.round(s.avgRtt)
    }));
}

function buildNetworkTimeline(samples: DetailsResponse['samples']) {
    if (!samples || samples.length === 0) return [];
    const step = Math.max(1, Math.floor(samples.length / 100));
    return samples.filter((_, i) => i % step === 0).map(s => {
        const rtt = s.avgRtt;
        return {
            time: new Date(s.createdAt).getTime(),
            fast: rtt <= 900 ? 1 : 0,
            medium: rtt > 900 && rtt <= 1600 ? 1 : 0,
            slow: rtt > 1600 ? 1 : 0
        };
    });
}

export function ContactDetailsPage({ token }: { token: string }) {
    const { contactId } = useParams();
    const { t } = useTranslation();
    const [range, setRange] = useState<Range>('24h');
    const [data, setData] = useState<DetailsResponse | null>(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);
    const [selectedDevice, setSelectedDevice] = useState<string | null>(null);
    const [deviceDropdownOpen, setDeviceDropdownOpen] = useState(false);

    const ranges: Range[] = ['1m', '5m', '15m', '30m', '1h', '12h', '24h', '7d', '30d'];

    useEffect(() => {
        async function load() {
            if (!contactId) return;
            setLoading(true);
            setError(null);
            try {
                let url = `${API_URL}/api/contacts/${contactId}/stats?range=${range}`;
                if (selectedDevice) {
                    url += `&deviceJid=${encodeURIComponent(selectedDevice)}`;
                }
                const res = await fetch(url, {
                    headers: { Authorization: `Bearer ${token}` }
                });
                const json = await res.json();
                if (!res.ok) throw new Error(json?.message || 'Failed to load details');
                setData(json);
            } catch (err) {
                setError(err instanceof Error ? err.message : 'Failed to load details');
            } finally {
                setLoading(false);
            }
        }
        load();
    }, [contactId, range, token, selectedDevice]);

    const knownDevices = data?.knownDevices ?? [];
    const hasMultipleDevices = knownDevices.length > 1;

    const networkTotal = useMemo(() => {
        if (!data) return 0;
        return data.network.fastStable + data.network.mediumMobile + data.network.slowUnstable;
    }, [data]);

    const heatmapData = useMemo(() => buildHeatmapData(data?.samples, range), [data?.samples, range]);
    const peakHoursData = useMemo(() => buildPeakHoursData(data?.samples), [data?.samples]);
    const rttTimeline = useMemo(() => buildRttTimeline(data?.samples), [data?.samples]);
    const networkTimeline = useMemo(() => buildNetworkTimeline(data?.samples), [data?.samples]);

    const contactName = data?.contact.customName || data?.contact.number || 'Contact';
    const currentStatus = data?.contact.lastKnownState || 'Unknown';

    return (
        <div className="min-h-screen bg-slate-950 text-white">
            <div className="max-w-7xl mx-auto px-6 py-8">
                {/* Header */}
                <div className="flex items-center justify-between mb-8">
                    <div className="flex items-center gap-4">
                        <Link
                            to="/dashboard"
                            className="p-2 rounded-lg bg-white/5 hover:bg-white/10 transition-colors"
                        >
                            <ArrowLeft size={20} />
                        </Link>
                        <div>
                            <h1 className="text-2xl font-bold">{t('details.title')}</h1>
                            <p className="text-white/50 text-sm">{contactName}</p>
                        </div>
                    </div>

                    {/* Profile + Status */}
                    <div className="flex items-center gap-4">
                        <div className="text-right">
                            <div className={`text-sm font-semibold ${
                                currentStatus.includes('Online') ? 'text-emerald-400' :
                                currentStatus === 'Standby' ? 'text-amber-400' :
                                currentStatus === 'OFFLINE' ? 'text-red-400' : 'text-white/40'
                            }`}>
                                {currentStatus}
                            </div>
                            <div className="text-xs text-white/30">{t('details.currentStatus')}</div>
                        </div>
                        <div className="relative">
                            <div className="w-12 h-12 rounded-full bg-white/10 overflow-hidden">
                                {data?.contact.profilePicUrl ? (
                                    <img src={data.contact.profilePicUrl} alt="" className="w-full h-full object-cover" />
                                ) : (
                                    <div className="w-full h-full flex items-center justify-center text-white/30 text-lg font-bold">
                                        {contactName[0]?.toUpperCase()}
                                    </div>
                                )}
                            </div>
                            <div className={`absolute -bottom-0.5 -right-0.5 w-4 h-4 rounded-full border-2 border-slate-950 ${
                                currentStatus.includes('Online') ? 'bg-emerald-400' :
                                currentStatus === 'Standby' ? 'bg-amber-400' :
                                currentStatus === 'OFFLINE' ? 'bg-red-400' : 'bg-white/20'
                            }`} />
                        </div>
                    </div>
                </div>

                {/* Device Selector */}
                {hasMultipleDevices && (
                    <div className="mb-6">
                        <div className="relative inline-block">
                            <button
                                onClick={() => setDeviceDropdownOpen(!deviceDropdownOpen)}
                                className="flex items-center gap-2 px-4 py-2.5 rounded-xl bg-white/[0.05] border border-white/10 hover:bg-white/[0.08] transition-colors text-sm"
                            >
                                <Smartphone size={14} className="text-cyan-400" />
                                <span className="text-white/70">
                                    {selectedDevice
                                        ? formatDeviceJid(selectedDevice)
                                        : t('details.allDevices', 'All Devices')}
                                </span>
                                <ChevronDown size={14} className={`text-white/40 transition-transform ${deviceDropdownOpen ? 'rotate-180' : ''}`} />
                            </button>

                            {deviceDropdownOpen && (
                                <>
                                    <div className="fixed inset-0 z-30" onClick={() => setDeviceDropdownOpen(false)} />
                                    <div className="absolute left-0 top-full mt-2 z-40 min-w-[200px] bg-slate-900 border border-white/10 rounded-xl shadow-2xl shadow-black/40 overflow-hidden">
                                        <button
                                            onClick={() => { setSelectedDevice(null); setDeviceDropdownOpen(false); }}
                                            className={`w-full text-left px-4 py-3 text-sm transition-colors flex items-center gap-2 ${
                                                !selectedDevice
                                                    ? 'bg-emerald-500/10 text-emerald-400'
                                                    : 'text-white/60 hover:bg-white/5 hover:text-white/80'
                                            }`}
                                        >
                                            <span className={`w-2 h-2 rounded-full ${!selectedDevice ? 'bg-emerald-400' : 'bg-white/20'}`} />
                                            {t('details.allDevices', 'All Devices')}
                                            <span className="ml-auto text-xs text-white/30">{knownDevices.length} devices</span>
                                        </button>
                                        <div className="h-px bg-white/5" />
                                        {knownDevices.map((dev) => (
                                            <button
                                                key={dev}
                                                onClick={() => { setSelectedDevice(dev); setDeviceDropdownOpen(false); }}
                                                className={`w-full text-left px-4 py-3 text-sm transition-colors flex items-center gap-2 ${
                                                    selectedDevice === dev
                                                        ? 'bg-cyan-500/10 text-cyan-400'
                                                        : 'text-white/60 hover:bg-white/5 hover:text-white/80'
                                                }`}
                                            >
                                                <span className={`w-2 h-2 rounded-full ${selectedDevice === dev ? 'bg-cyan-400' : 'bg-white/20'}`} />
                                                {formatDeviceJid(dev)}
                                                <span className="ml-auto text-[10px] text-white/20 font-mono truncate max-w-[100px]">
                                                    {dev.split('@')[0]}
                                                </span>
                                            </button>
                                        ))}
                                    </div>
                                </>
                            )}
                        </div>

                        {selectedDevice && (
                            <span className="ml-3 text-xs text-cyan-400/60">
                                {t('details.filteringByDevice', 'Filtering by')} {formatDeviceJid(selectedDevice)}
                            </span>
                        )}
                    </div>
                )}

                {/* Time Range Pills */}
                <div className="flex gap-2 mb-8 overflow-x-auto pb-2">
                    {ranges.map(r => (
                        <button
                            key={r}
                            onClick={() => setRange(r)}
                            className={`px-4 py-2 rounded-lg text-sm font-medium whitespace-nowrap transition-all ${
                                range === r
                                    ? 'bg-gradient-to-r from-emerald-500 to-cyan-500 text-white shadow-lg shadow-emerald-500/25'
                                    : 'bg-white/5 text-white/50 hover:bg-white/10 hover:text-white/70'
                            }`}
                        >
                            {t(`details.timeRanges.${r}`)}
                        </button>
                    ))}
                </div>

                {loading && (
                    <div className="flex items-center justify-center py-20">
                        <div className="w-8 h-8 border-2 border-emerald-400 border-t-transparent rounded-full animate-spin" />
                        <span className="ml-3 text-white/50">{t('details.loading')}</span>
                    </div>
                )}

                {error && (
                    <div className="bg-red-500/10 border border-red-500/20 rounded-xl p-6 text-red-400">{error}</div>
                )}

                {!loading && !error && data && (
                    <>
                        {data.summary ? (
                            <div className="space-y-6">
                                {/* Stats Grid */}
                                <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
                                    <div className="bg-white/[0.03] border border-white/10 rounded-2xl p-5">
                                        <div className="flex items-center gap-2 mb-2">
                                            <Activity size={14} className="text-emerald-400" />
                                            <span className="text-xs text-white/40">{t('details.avgRtt')}</span>
                                        </div>
                                        <div className="text-2xl font-bold">{data.summary.avgRtt.toFixed(0)} <span className="text-sm text-white/30">ms</span></div>
                                    </div>

                                    <div className="bg-white/[0.03] border border-white/10 rounded-2xl p-5 flex items-center justify-center">
                                        <ProgressRing value={data.summary.onlinePct} color="#34d399" label={t('details.onlinePct')} size={90} />
                                    </div>
                                    <div className="bg-white/[0.03] border border-white/10 rounded-2xl p-5 flex items-center justify-center">
                                        <ProgressRing value={data.summary.standbyPct} color="#fbbf24" label={t('details.standbyPct')} size={90} />
                                    </div>
                                    <div className="bg-white/[0.03] border border-white/10 rounded-2xl p-5 flex items-center justify-center">
                                        <ProgressRing value={data.summary.offlinePct} color="#f87171" label={t('details.offlinePct')} size={90} />
                                    </div>

                                    <div className="bg-white/[0.03] border border-white/10 rounded-2xl p-5">
                                        <div className="flex items-center gap-2 mb-2">
                                            <BarChart3 size={14} className="text-cyan-400" />
                                            <span className="text-xs text-white/40">{t('details.samples')}</span>
                                        </div>
                                        <div className="text-2xl font-bold">{data.summary.sampleCount.toLocaleString()}</div>
                                    </div>
                                </div>

                                {/* RTT Over Time */}
                                {rttTimeline.length > 1 && (
                                    <div className="bg-white/[0.03] border border-white/10 rounded-2xl p-6">
                                        <h3 className="text-sm font-semibold text-white/60 mb-4 flex items-center gap-2">
                                            <Signal size={14} />
                                            {t('details.rttOverTime')}
                                        </h3>
                                        <div className="h-[250px]">
                                            <ResponsiveContainer width="100%" height="100%">
                                                <LineChart data={rttTimeline}>
                                                    <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.05)" vertical={false} />
                                                    <XAxis
                                                        dataKey="time" type="number" domain={['auto', 'auto']} scale="time"
                                                        tickFormatter={(v) => new Date(v).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                                                        stroke="rgba(255,255,255,0.15)" tick={{ fill: 'rgba(255,255,255,0.3)', fontSize: 11 }}
                                                    />
                                                    <YAxis stroke="rgba(255,255,255,0.15)" tick={{ fill: 'rgba(255,255,255,0.3)', fontSize: 11 }} />
                                                    <Tooltip
                                                        labelFormatter={(v) => new Date(v as number).toLocaleString()}
                                                        contentStyle={{ background: '#1e293b', border: 'none', borderRadius: '8px', color: '#fff' }}
                                                        labelStyle={{ color: 'rgba(255,255,255,0.5)' }}
                                                    />
                                                    <Line type="monotone" dataKey="rtt" stroke="#34d399" strokeWidth={2} dot={false} name="RTT (ms)" isAnimationActive={false} />
                                                </LineChart>
                                            </ResponsiveContainer>
                                        </div>
                                    </div>
                                )}

                                {/* Activity Heatmap */}
                                {heatmapData.length > 0 && range !== '1m' && range !== '5m' && range !== '15m' && range !== '30m' && range !== '1h' && (
                                    <div className="bg-white/[0.03] border border-white/10 rounded-2xl p-6">
                                        <h3 className="text-sm font-semibold text-white/60 mb-4">{t('details.activityHeatmap')}</h3>
                                        <div className="overflow-x-auto">
                                            <div className="min-w-[700px]">
                                                <div className="grid grid-cols-[60px_repeat(24,1fr)] gap-1">
                                                    <div />
                                                    {Array.from({ length: 24 }, (_, h) => (
                                                        <div key={h} className="text-[10px] text-white/30 text-center">{String(h).padStart(2, '0')}</div>
                                                    ))}
                                                    {['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'].map(day => (
                                                        <React.Fragment key={day}>
                                                            <div className="text-xs text-white/40 flex items-center">{day}</div>
                                                            {Array.from({ length: 24 }, (_, h) => {
                                                                const cell = heatmapData.find(c => c.day === day && c.hour === h);
                                                                const intensity = cell?.intensity || 0;
                                                                return (
                                                                    <div
                                                                        key={h}
                                                                        className="aspect-square rounded-sm transition-colors"
                                                                        style={{
                                                                            backgroundColor: intensity > 0
                                                                                ? `rgba(52, 211, 153, ${Math.max(0.1, intensity / 100)})`
                                                                                : 'rgba(255,255,255,0.03)'
                                                                        }}
                                                                        title={`${day} ${String(h).padStart(2, '0')}:00 — ${intensity}% online`}
                                                                    />
                                                                );
                                                            })}
                                                        </React.Fragment>
                                                    ))}
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                )}

                                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                                    {/* Peak Hours */}
                                    {peakHoursData.length > 0 && (
                                        <div className="bg-white/[0.03] border border-white/10 rounded-2xl p-6">
                                            <h3 className="text-sm font-semibold text-white/60 mb-4 flex items-center gap-2">
                                                <Clock size={14} />
                                                {t('details.peakHours')}
                                            </h3>
                                            <div className="h-[200px]">
                                                <ResponsiveContainer width="100%" height="100%">
                                                    <BarChart data={peakHoursData}>
                                                        <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.05)" vertical={false} />
                                                        <XAxis dataKey="hour" stroke="rgba(255,255,255,0.15)" tick={{ fill: 'rgba(255,255,255,0.3)', fontSize: 10 }} interval={2} />
                                                        <YAxis stroke="rgba(255,255,255,0.15)" tick={{ fill: 'rgba(255,255,255,0.3)', fontSize: 10 }} />
                                                        <Tooltip contentStyle={{ background: '#1e293b', border: 'none', borderRadius: '8px', color: '#fff' }} />
                                                        <Bar dataKey="count" fill="#34d399" radius={[3, 3, 0, 0]} name="Online samples" />
                                                    </BarChart>
                                                </ResponsiveContainer>
                                            </div>
                                        </div>
                                    )}

                                    {/* Network Quality Over Time */}
                                    {networkTimeline.length > 1 && (
                                        <div className="bg-white/[0.03] border border-white/10 rounded-2xl p-6">
                                            <h3 className="text-sm font-semibold text-white/60 mb-4 flex items-center gap-2">
                                                <Wifi size={14} />
                                                {t('details.networkOverTime')}
                                            </h3>
                                            <div className="h-[200px]">
                                                <ResponsiveContainer width="100%" height="100%">
                                                    <AreaChart data={networkTimeline}>
                                                        <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.05)" vertical={false} />
                                                        <XAxis dataKey="time" hide />
                                                        <YAxis stroke="rgba(255,255,255,0.15)" tick={{ fill: 'rgba(255,255,255,0.3)', fontSize: 10 }} />
                                                        <Tooltip contentStyle={{ background: '#1e293b', border: 'none', borderRadius: '8px', color: '#fff' }} />
                                                        <Area type="monotone" dataKey="fast" stackId="1" stroke="#34d399" fill="#34d399" fillOpacity={0.3} name={t('details.fastWifi')} isAnimationActive={false} />
                                                        <Area type="monotone" dataKey="medium" stackId="1" stroke="#fbbf24" fill="#fbbf24" fillOpacity={0.3} name={t('details.mediumMobile')} isAnimationActive={false} />
                                                        <Area type="monotone" dataKey="slow" stackId="1" stroke="#f87171" fill="#f87171" fillOpacity={0.3} name={t('details.slowUnstable')} isAnimationActive={false} />
                                                    </AreaChart>
                                                </ResponsiveContainer>
                                            </div>
                                        </div>
                                    )}
                                </div>

                                <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                                    {/* Today's Activity */}
                                    <div className="bg-white/[0.03] border border-white/10 rounded-2xl p-6">
                                        <h3 className="text-sm font-semibold text-white/60 mb-4 flex items-center gap-2">
                                            <Clock size={14} />
                                            {t('details.todayActivity')}
                                        </h3>
                                        <div className="space-y-3 text-sm">
                                            <div className="flex justify-between">
                                                <span className="text-white/40">{t('details.firstConnection')}</span>
                                                <span className="font-medium">
                                                    {data.summary.firstOnlineToday
                                                        ? new Date(data.summary.firstOnlineToday).toLocaleTimeString()
                                                        : t('details.noData')}
                                                </span>
                                            </div>
                                            <div className="flex justify-between">
                                                <span className="text-white/40">{t('details.lastConnection')}</span>
                                                <span className="font-medium">
                                                    {data.summary.lastOnlineToday
                                                        ? new Date(data.summary.lastOnlineToday).toLocaleTimeString()
                                                        : t('details.noData')}
                                                </span>
                                            </div>
                                            <div className="flex justify-between">
                                                <span className="text-white/40">{t('details.peakHour')}</span>
                                                <span className="font-medium">
                                                    {data.summary.peakHour !== null
                                                        ? `${String(data.summary.peakHour).padStart(2, '0')}:00`
                                                        : t('details.noData')}
                                                </span>
                                            </div>
                                        </div>
                                    </div>

                                    {/* Network Pattern */}
                                    <div className="bg-white/[0.03] border border-white/10 rounded-2xl p-6">
                                        <h3 className="text-sm font-semibold text-white/60 mb-4 flex items-center gap-2">
                                            <Smartphone size={14} />
                                            {t('details.networkPattern')}
                                        </h3>
                                        <div className="space-y-3">
                                            {[
                                                { label: t('details.fastWifi'), val: data.network.fastStable, color: 'bg-emerald-400' },
                                                { label: t('details.mediumMobile'), val: data.network.mediumMobile, color: 'bg-amber-400' },
                                                { label: t('details.slowUnstable'), val: data.network.slowUnstable, color: 'bg-red-400' }
                                            ].map(n => {
                                                const pct = networkTotal > 0 ? (n.val / networkTotal) * 100 : 0;
                                                return (
                                                    <div key={n.label}>
                                                        <div className="flex justify-between text-xs mb-1">
                                                            <span className="text-white/40">{n.label}</span>
                                                            <span className="text-white/60">{pct.toFixed(1)}%</span>
                                                        </div>
                                                        <div className="h-1.5 bg-white/5 rounded-full overflow-hidden">
                                                            <div className={`h-full ${n.color} rounded-full transition-all duration-700`} style={{ width: `${pct}%` }} />
                                                        </div>
                                                    </div>
                                                );
                                            })}
                                        </div>
                                    </div>

                                    {/* Sleep / Inactivity */}
                                    <div className="bg-white/[0.03] border border-white/10 rounded-2xl p-6">
                                        <h3 className="text-sm font-semibold text-white/60 mb-4 flex items-center gap-2">
                                            <Moon size={14} />
                                            {t('details.sleepWindows')}
                                        </h3>
                                        {data.inactivityWindows.length === 0 ? (
                                            <p className="text-xs text-white/30">{t('details.noInactivity')}</p>
                                        ) : (
                                            <div className="space-y-2">
                                                {data.inactivityWindows.slice(0, 4).map((w, idx) => (
                                                    <div key={idx} className="flex items-center gap-2 text-xs">
                                                        <div className="w-1 h-8 rounded-full bg-violet-500/50" />
                                                        <div>
                                                            <div className="text-white/60">
                                                                {new Date(w.start).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                                                                {' → '}
                                                                {new Date(w.end).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                                                            </div>
                                                            <div className="text-white/30">{w.hours.toFixed(1)}h</div>
                                                        </div>
                                                    </div>
                                                ))}
                                            </div>
                                        )}
                                    </div>
                                </div>

                                {/* Insights */}
                                {data.insights.length > 0 && (
                                    <div className="bg-white/[0.03] border border-white/10 rounded-2xl p-6">
                                        <h3 className="text-sm font-semibold text-white/60 mb-4">{t('details.insights')}</h3>
                                        <ul className="space-y-2">
                                            {data.insights.map((insight, i) => (
                                                <li key={i} className="flex items-start gap-2 text-sm text-white/70">
                                                    <span className="text-emerald-400 mt-0.5">•</span>
                                                    {insight}
                                                </li>
                                            ))}
                                        </ul>
                                    </div>
                                )}
                            </div>
                        ) : (
                            <div className="bg-white/[0.03] border border-white/10 rounded-2xl p-12 text-center">
                                <div className="w-16 h-16 rounded-full bg-white/5 flex items-center justify-center mx-auto mb-4">
                                    <BarChart3 size={24} className="text-white/20" />
                                </div>
                                <p className="text-white/40">{t('details.notEnoughData')}</p>
                            </div>
                        )}
                    </>
                )}
            </div>
        </div>
    );
}
